/* eslint-disable no-constant-condition */
import DefaultImageView from 'components/common/DefaultImageView';
import DefaultText from 'components/common/DefaultText';
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import {
  loadTossPayments,
  TossPaymentsInstance,
} from '@tosspayments/payment-sdk';
import {useRecoilValue} from 'recoil';
import {baseUrlTypeState, userInfoState} from 'utils/Atoms';
import {ITongPlacePayment} from 'interfaces/interface';
import {searchPlans} from 'function/api';
import DefaultButton from 'components/common/DefaultButton';
import SubscribePlanModal from 'components/paymentManagement/SubscribePlanModal';

const clientKey = process.env
  .REACT_APP_TOSS_PAYMENTS_CLIENT_SECRET_KEY as string;

/**
 * 결제 정보 카드 컴포넌트의 Props 타입
 * @property {ITongPlacePayment | null} infoData - 결제 정보 데이터
 * @property {React.Dispatch<React.SetStateAction<string>>} setModal - 모달 상태 설정 함수
 * @property {React.Dispatch<React.SetStateAction<{}>>} setErrorModal - 에러 모달 설정 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading - 로딩 상태 설정 함수
 */
type Props = {
  infoData: ITongPlacePayment | null;
  setModal: React.Dispatch<React.SetStateAction<string>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * 카드 등록 인터페이스
 * @property {any} tossPayments - 토스페이먼츠 인스턴스
 * @property {string} tongPlaceUUID - 통플레이스 UUID
 * @property {React.Dispatch<React.SetStateAction<string>>} setModal - 모달 상태 설정 함수
 * @property {'prod' | 'dev'} baseUrlType - 베이스 URL 타입
 */
interface IRegisterCard {
  tossPayments: any;
  tongPlaceUUID: string;
  setModal: React.Dispatch<React.SetStateAction<string>>;
  baseUrlType: 'prod' | 'dev';
}

/**
 * 카드 등록 함수
 * 토스페이먼츠를 사용하여 카드를 등록합니다.
 * @param {IRegisterCard} params - 카드 등록 파라미터
 * @param {any} params.tossPayments - 토스페이먼츠 인스턴스
 * @param {string} params.tongPlaceUUID - 통플레이스 UUID
 * @param {React.Dispatch<React.SetStateAction<string>>} params.setModal - 모달 상태 설정 함수
 * @param {'prod' | 'dev'} params.baseUrlType - 베이스 URL 타입
 * @returns {Promise<void>}
 */
export const registerCard = async ({
  tossPayments,
  tongPlaceUUID,
  setModal,
  baseUrlType,
}: IRegisterCard) => {
  // 테스트 1
  // const successUrl = `${BASEURL}/v1/payments/test/request-billing-auth/success`;
  // const failUrl = `${BASEURL}/v1/payments/test/request-billing-auth/fail`;

  // 테스트 2
  // const successUrl = `http://localhost:3000/paymentManagement?isSuccess=true`;
  // const failUrl = `http://localhost:3000/paymentManagement?isSuccess=false`;

  // 통플레이스 plan 오더 주문 플랜 등록하는 api
  const BASEURL = process.env.REACT_APP_BASEURL;
  const successUrl = `${BASEURL}/v1/tong-places/payment-method/success`;
  const failUrl = `${BASEURL}/v1/tong-places/payment-method/fail`;

  try {
    if (tossPayments) {
      await tossPayments.requestBillingAuth('카드', {
        customerKey: tongPlaceUUID,
        successUrl,
        failUrl,
      });
    }
  } catch (error: any) {
    if (error?.code) {
      if (error.code === 'USER_CANCEL') {
        // 결제 고객이 결제창을 닫았을 때 에러 처리
      } else if (error.code === 'INVALID_CARD_COMPANY') {
        // 유효하지 않은 카드 코드에 대한 에러 처리
        setModal('cardFail');
      } else {
        setModal('cardFail');
      }
    }
  }
};

/**
 * 플랜 목록 기본 데이터
 * 기본, 스탠다드, 프리미엄 플랜의 기본 정보를 담고 있는 배열입니다.
 */
export const planListBase = [
  {
    title: 'Basic',
    value: 'Free',
    color: colors.gray,
    valueColor: colors.graniteGray,
    checkImg: 'basic_check',
    detailList: [
      'planDetailOne',
      'basicDetailTwo',
      'basicDetailThree',
      'basicDetailFour',
    ],
  },
  {
    title: 'Standard',
    valueImg: 'won_blue',
    color: colors.primary2,
    valueColor: '#192C3E',
    checkImg: 'standard_check',
    detailList: [
      'planDetailOne',
      'standardDetailTwo',
      'standardDetailThree',
      'standardDetailFour',
    ],
    warnMsg: 'standardWarnMsg',
  },
  {
    title: 'Premium',
    valueImg: 'won_red',
    color: '#B4534D',
    valueColor: '#502828',
    valueDetail: ['premiumValueDetailOne', 'premiumValueDetailTwo'],
    checkImg: 'premium_check',
    detailList: [
      'premiumDetailOne',
      'premiumDetailTwo',
      'premiumDetailThree',
      'premiumDetailFour',
    ],
  },
];

/**
 * 결제 정보 카드 컴포넌트
 * 결제 정보를 표시하는 카드 컴포넌트입니다.
 * @param {Props} props - 결제 정보 카드 컴포넌트의 props
 * @returns {JSX.Element} 결제 정보 카드 컴포넌트
 */
const InfoCard = ({infoData, setModal, setErrorModal, setIsLoading}: Props) => {
  const [plansData, setPlansData] = useState<any[] | null>(null);

  const userInfo = useRecoilValue(userInfoState);
  const baseUrlType = useRecoilValue(baseUrlTypeState);

  const [tossPayments, setTossPayments] = useState<TossPaymentsInstance | null>(
    null,
  );

  const [subscribePlanModalOpen, setSubscribePlanModalOpen] = useState(false);

  useEffect(() => {
    const setTossPaymentsInstance = async () => {
      const tossPaymentsInstance = await loadTossPayments(clientKey);
      setTossPayments(tossPaymentsInstance);
    };
    setTossPaymentsInstance();
  }, []);

  useEffect(() => {
    searchPlans({userInfo, setIsLoading, setErrorModal}).then(plans => {
      if (plans) {
        const sortData = plans.sort((a, b) => a.price - b.price);

        for (let i = 0; i < 3; i++) {
          sortData[i] = {...sortData[i], ...planListBase[i]};
        }

        plans && setPlansData(sortData);
      }
    });
  }, []);

  return (
    <Container>
      <Main>
        <DefaultText text="currentPaymentInfo" textStyle={HeaderTextStyle} />

        <CardWrap>
          {infoData ? (
            <>
              <DisplayRow>
                <CurrentPaymentInfoSession>
                  <DefaultText
                    text="paymentClassification"
                    textStyle={CategoryTextStyle}
                  />
                  <CurrentPaymentInfoDetailContainer>
                    <DefaultText
                      text={'프리미엄 플랜 + AI 지원'}
                      textStyle={currentPaymentInfoDetailTextStyle}
                    />
                    <DefaultImageView
                      source="images/paymentManagement/setting.png"
                      style={{width: minmax(20), cursor: 'pointer'}}
                      onClick={() => {
                        setSubscribePlanModalOpen(true);
                      }}
                    />
                  </CurrentPaymentInfoDetailContainer>
                </CurrentPaymentInfoSession>
                <CurrentPaymentInfoSession>
                  <DefaultText
                    text="paymentAmount"
                    textStyle={CategoryTextStyle}
                  />
                  <CurrentPaymentInfoDetailContainer>
                    <DefaultText
                      text={'총 220,000원 / 월'}
                      textStyle={{
                        ...currentPaymentInfoDetailTextStyle,
                        fontSize: minmax(28),
                        fontWeight: 'bold',
                      }}
                    />
                  </CurrentPaymentInfoDetailContainer>
                </CurrentPaymentInfoSession>
              </DisplayRow>
              <Border />
              <DisplayRow>
                <CurrentPaymentInfoSession>
                  <DefaultText
                    text="creditCardInfo"
                    textStyle={CategoryTextStyle}
                  />
                  <CurrentPaymentInfoDetailContainer>
                    <DefaultText
                      text={'****-****-****-1234'}
                      textStyle={currentPaymentInfoDetailTextStyle}
                    />
                    <DefaultImageView
                      source="images/paymentManagement/setting.png"
                      style={{width: minmax(20), cursor: 'pointer'}}
                      onClick={() =>
                        registerCard({
                          tossPayments: tossPayments,
                          tongPlaceUUID: 'aaa',
                          setModal,
                          baseUrlType,
                        })
                      }
                    />
                  </CurrentPaymentInfoDetailContainer>
                </CurrentPaymentInfoSession>
                <CurrentPaymentInfoSession>
                  <DefaultText
                    text="nextRecurringPaymentDate"
                    textStyle={CategoryTextStyle}
                  />
                  <CurrentPaymentInfoDetailContainer>
                    <DefaultText
                      text={'2024.10.20에 취소'}
                      textStyle={currentPaymentInfoDetailTextStyle}
                    />
                  </CurrentPaymentInfoDetailContainer>
                </CurrentPaymentInfoSession>
              </DisplayRow>
              <DisplayRow>
                <CurrentPaymentInfoSession>
                  <DefaultText
                    text="paymentManager"
                    textStyle={CategoryTextStyle}
                  />
                  <CurrentPaymentInfoDetailContainer>
                    <DefaultText
                      text={'방윤정'}
                      textStyle={currentPaymentInfoDetailTextStyle}
                    />
                  </CurrentPaymentInfoDetailContainer>
                </CurrentPaymentInfoSession>
                <CurrentPaymentInfoSession>
                  <DefaultText
                    text="receiptEmail"
                    textStyle={CategoryTextStyle}
                  />
                  <CurrentPaymentInfoDetailContainer>
                    <DefaultText
                      text={'tong2302@naver.com'}
                      textStyle={currentPaymentInfoDetailTextStyle}
                    />
                    <DefaultImageView
                      source="images/common/pencil.png"
                      style={{height: minmax(29), cursor: 'pointer'}}
                      onClick={() => {
                        setModal('email');
                      }}
                    />
                  </CurrentPaymentInfoDetailContainer>
                </CurrentPaymentInfoSession>
              </DisplayRow>
            </>
          ) : (
            <NoPaymentInfoContainer>
              <DefaultText
                text="noSubscribeInfo"
                textStyle={{
                  color: colors.graniteGray,
                  fontSize: minmax(20),
                }}
              />
              <DefaultButton
                text={'subscribePlan'}
                buttonStyle={{
                  width: minmax(280),
                  height: minmax(60),
                  backgroundColor: '#31577a',
                }}
                textStyle={{
                  color: colors.white,
                  fontSize: minmax(20),
                }}
                onClick={() => setSubscribePlanModalOpen(true)}
              />
            </NoPaymentInfoContainer>
          )}
        </CardWrap>
      </Main>

      {/* 플랜 구독하기 모달 */}
      {subscribePlanModalOpen && (
        <SubscribePlanModal
          closeModal={() => setSubscribePlanModalOpen(false)}
          plansData={plansData}
        />
      )}
    </Container>
  );
};

export default InfoCard;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  column-gap: ${minmax(40)};
  margin-bottom: ${minmax(60)};
`;

const Main = styled.div`
  width: 100%;
`;

const HeaderTextStyle = {
  width: 'fit-content',
  fontWeight: 500,
  borderBottom: `3px solid ${colors.primary}`,
  alignSelf: 'flex-start',
  marginBottom: minmax(24),
};

const CardWrap = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${minmax(16)} 3%;
  row-gap: ${minmax(16)};
  border-radius: ${minmax(10)};
  border: 1px solid ${colors.borderGray};
  background-color: ${colors.whiteSmoke};
`;

const DisplayRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CategoryTextStyle = {
  width: 'max(100px, min(10vw, 200px))',
  color: colors.darkGray,
};

const Border = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.borderGray};
`;

const NoPaymentInfoContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  flex-direction: column;
  height: ${minmax(125)};
`;

const CurrentPaymentInfoSession = styled.div`
  display: flex;
  width: 50%;
  align-items: center;
`;

const CurrentPaymentInfoDetailContainer = styled.div`
  display: flex;
  column-gap: ${minmax(10)};
  align-items: center;
  justify-content: center;
`;

const currentPaymentInfoDetailTextStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
